'use client';

import supportContact from '@/lib/constants/support-contact.json';

import { useSiteConfig } from '@/context/SiteConfigContext';
import { siteConfig as seoFallback } from '@/lib/seo-config';
import { PRIMARY_ENTITY_NAME } from '@/lib/tenant/canonical';

/**
 * Helper: Build an array of social media URLs from the SiteConfig social object.
 * Filters out empty strings so only configured platforms appear.
 */
/** Where a bare @handle lives, per platform, when the DB stores a handle rather than a URL. */
const SOCIAL_HOSTS: Record<string, string> = {
  facebook: 'https://www.facebook.com/',
  twitter: 'https://x.com/',
  instagram: 'https://www.instagram.com/',
  pinterest: 'https://www.pinterest.com/',
  youtube: 'https://www.youtube.com/@',
  linkedin: 'https://www.linkedin.com/company/',
};

/**
 * `sameAs` must contain absolute profile URLs — that is the whole point of the
 * property: it links the entity to its profiles so Google and AI engines can
 * reconcile them into one Knowledge Graph entity.
 *
 * The admin settings fields accept whatever the operator types, and production
 * was emitting `sameAs: ["@smartblindshub"]` — a bare handle, which is not a
 * URL, resolves to nothing, and makes the whole property invalid. Normalize
 * handles to real URLs and drop anything that still isn't one.
 */
function buildSocialUrls(social: {
  facebook: string;
  instagram: string;
  twitter: string;
  youtube: string;
  pinterest: string;
  linkedin: string;
}): string[] {
  const order: (keyof typeof social)[] = [
    'facebook', 'twitter', 'instagram', 'pinterest', 'youtube', 'linkedin',
  ];

  return order
    .map((platform) => {
      const raw = (social[platform] || '').trim();
      if (!raw) return '';
      if (/^https?:\/\//i.test(raw)) return raw;
      // Protocol-relative or bare domain (e.g. "www.facebook.com/x")
      if (raw.startsWith('//')) return `https:${raw}`;
      if (/^[\w.-]+\.[a-z]{2,}\//i.test(raw)) return `https://${raw}`;
      // Bare handle, with or without the leading @
      const handle = raw.replace(/^@/, '');
      if (!handle || /[\s/]/.test(handle)) return '';
      return `${SOCIAL_HOSTS[platform] ?? ''}${handle}`;
    })
    .filter((u) => /^https:\/\/[^\s]+\.[a-z]{2,}/i.test(u));
}

/** Schema/entity name: the tenant brand without the ™ glyph ("Smart Blinds Pro"). */
function entityName(raw: string): string {
  return (raw || '').replace(/[™®]/g, '').replace(/\s+/g, ' ').trim();
}

/**
 * Names people and AI engines use for this business. Used to separate it from
 * the unrelated Dutch company Smartblinds B.V. (smartblinds.com), which owns
 * the bare "Smart Blinds" query.
 */
function alternateNames(name: string, rawName: string, url: string): string[] {
  const host = url.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
  const out = [rawName, host];
  if (/smartblindspro\.com$/i.test(host)) out.push('SmartBlindsPro', 'Smart Blinds Pro Redmond', 'Smart Blinds', 'Smart Blinds LLC');
  return Array.from(new Set(out.filter((n) => n && n !== name)));
}

/** "564-262-6915" -> "+1-564-262-6915" (E.164-style for schema). */
function schemaPhone(phone: string): string {
  const d = (phone || '').replace(/\D/g, '');
  if (d.length === 10) return `+1-${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11 && d.startsWith('1')) return `+1-${d.slice(1, 4)}-${d.slice(4, 7)}-${d.slice(7)}`;
  return phone;
}

/** Parse "Mon-Fri: 8AM-6PM PT" style strings from the DB; null when unparseable. */
function parseHours(text: string): { opens: string; closes: string } | null {
  const m = (text || '').match(/(\d{1,2})(?::(\d{2}))?\s*(AM|PM)\s*[-–]\s*(\d{1,2})(?::(\d{2}))?\s*(AM|PM)/i);
  if (!m) return null;
  const to24 = (h: string, min: string | undefined, ap: string) => {
    let hh = parseInt(h, 10) % 12;
    if (ap.toUpperCase() === 'PM') hh += 12;
    return `${String(hh).padStart(2, '0')}:${min || '00'}`;
  };
  return { opens: to24(m[1], m[2], m[3]), closes: to24(m[4], m[5], m[6]) };
}

const hostOf = (u: string) => (u || '').replace(/^https?:\/\//, '').replace(/\/.*$/, '').toLowerCase();

/**
 * SEO identity for schema. Sister brands already canonical to the primary
 * domain, but their JSON-LD used to declare a separate Organization/WebSite on
 * their own host ("Shade and Blinds", shadeandblinds.com/#organization) with
 * the same phone and address, which contradicts the canonical and gave search
 * and AI engines a second entity to cite. On a consolidated brand the entity
 * is now the primary one (same @id/url as the canonical) and the sister brand
 * name/host are kept as alternateName. Visible page branding is unchanged.
 */
function seoIdentity(cfg: { siteUrl?: string; seoSiteUrl?: string; companyName?: string }) {
  const tenantUrl = cfg.siteUrl || seoFallback.url;
  const url = cfg.seoSiteUrl || tenantUrl;
  const tenantRaw = cfg.companyName || seoFallback.name;
  const consolidated = hostOf(url) !== hostOf(tenantUrl);
  const rawName = consolidated ? PRIMARY_ENTITY_NAME : tenantRaw;
  const name = entityName(rawName);
  const alt = alternateNames(name, rawName, url);
  const alternateName = consolidated
    ? Array.from(new Set([...alt, entityName(tenantRaw), hostOf(tenantUrl)].filter((n) => n && n !== name)))
    : alt;
  return { url, rawName, name, alternateName };
}

const FOUNDING_YEAR = '2021';
const EASTSIDE_CITIES = ['Redmond', 'Bellevue', 'Kirkland', 'Sammamish', 'Issaquah', 'Woodinville', 'Bothell'];

// Organization Schema
export function OrganizationJsonLd() {
  const cfg = useSiteConfig();

  // Derive values from context with seo-config fallback
  const { url, name, alternateName } = seoIdentity(cfg);
  const legalName = cfg.companyLegalName || name;
  const description = cfg.description || seoFallback.description;
  const phone = cfg.phone || seoFallback.phone;
  const logoUrl = cfg.logoUrl
    ? (cfg.logoUrl.startsWith('http') ? cfg.logoUrl : `${url}${cfg.logoUrl}`)
    : `${url}${seoFallback.logo}`;
  const email = cfg.email?.sales || cfg.email?.support || seoFallback.email;

  const socialUrls = buildSocialUrls(cfg.social);

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${url}/#organization`,
    name,
    legalName,
    alternateName,
    // Entity disambiguation for search and AI answers: a different company,
    // Smartblinds B.V. (Netherlands, smartblinds.com), uses a similar name.
    disambiguatingDescription: `${name} is operated by Smart Blinds LLC, a company registered in Washington with a repair warehouse in Redmond, WA. Smart Blinds is a registered trademark owned by Smart Blinds LLC. The business sells made-to-measure blinds and shades online across the United States and offers local consultation and installation on Washington’s Eastside. It is not affiliated with Smartblinds B.V. of the Netherlands.`,
    url,
    logo: logoUrl,
    description,
    foundingDate: FOUNDING_YEAR,
    foundingLocation: {
      '@type': 'Place',
      name: 'Redmond, Washington, United States',
    },
    ...(phone ? { telephone: schemaPhone(phone) } : {}),
    ...(email ? { email } : {}),
    address: {
      '@type': 'PostalAddress',
      ...(cfg.address.street && { streetAddress: cfg.address.street }),
      ...(cfg.address.city && { addressLocality: cfg.address.city }),
      ...(cfg.address.state && { addressRegion: cfg.address.state }),
      ...(cfg.address.zip && { postalCode: cfg.address.zip }),
      addressCountry: 'US',
    },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: supportContact.phoneInternational,
        contactType: 'customer service',
        availableLanguage: ['English'],
        areaServed: 'US',
      },
      {
        '@type': 'ContactPoint',
        telephone: schemaPhone(phone),
        contactType: 'sales',
        availableLanguage: ['English'],
        areaServed: 'US',
      },
    ],
    areaServed: {
      '@type': 'Country',
      name: 'United States',
    },
    brand: {
      '@type': 'Brand',
      name,
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: `${name} Product Line`,
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Custom Motorized Blinds and Shades',
          },
          areaServed: 'US',
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Professional Window Treatment Installation',
          },
          areaServed: EASTSIDE_CITIES.map((c) => ({ '@type': 'City', name: `${c}, WA` })),
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'In-Home Window Treatment Consultation',
          },
          areaServed: EASTSIDE_CITIES.map((c) => ({ '@type': 'City', name: `${c}, WA` })),
        },
      ],
    },
    // Omit the property entirely when there are no valid profile URLs — an
    // empty `sameAs: []` is noise, and a `sameAs` listing a profile that 404s
    // is actively worse than none for entity reconciliation.
    ...(socialUrls.length > 0 ? { sameAs: socialUrls } : {}),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// Website Schema
export function WebsiteJsonLd() {
  const cfg = useSiteConfig();

  const { url, name, alternateName } = seoIdentity(cfg);
  const description = cfg.description || seoFallback.description;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    alternateName,
    '@id': `${url}/#website`,
    url,
    name,
    description,
    publisher: {
      '@id': `${url}/#organization`,
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${url}/products?search={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
    inLanguage: 'en-US',
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// Local Business Schema
// IMPORTANT: address + geo are required for Google "near me" results
export function LocalBusinessJsonLd() {
  const cfg = useSiteConfig();

  const { url, name, alternateName } = seoIdentity(cfg);
  const phone = cfg.phone || seoFallback.phone;
  // Public business email = sales/contact address from the DB (e.g.
  // sales@smartblindspro.com), not the internal support inbox. DB-driven.
  const email = cfg.email?.sales || cfg.email?.support || seoFallback.email;
  const logoUrl = cfg.logoUrl
    ? (cfg.logoUrl.startsWith('http') ? cfg.logoUrl : `${url}${cfg.logoUrl}`)
    : `${url}${seoFallback.logo}`;

  // Address: prefer database values, fall back to the public mailing address.
  // This is a service-area business (we go to the customer), so there is no
  // walk-in street address; the P.O. Box is the only public address.
  const streetAddress = cfg.address?.street || 'P.O. Box 166';
  const addressLocality = cfg.address?.city || 'Kirkland';
  const addressRegion = cfg.address?.state || 'WA';
  const postalCode = cfg.address?.zip || '98083';
  const weekdayHours = parseHours(cfg.hours?.weekday || '');
  const saturdayHours = parseHours(cfg.hours?.saturday || '');

  const schema = {
    '@context': 'https://schema.org',
    // No 'Store': there is no walk-in storefront (service-area business).
    '@type': 'HomeAndConstructionBusiness',
    '@id': `${url}/#localbusiness`,
    name,
    alternateName,
    parentOrganization: { '@id': `${url}/#organization` },
    description: `${name} designs and sells custom window blinds, shades and motorized smart shades from Redmond, Washington. Free in-home design consultation and professional installation in Redmond, Bellevue, Kirkland, Sammamish, Issaquah, Woodinville and Bothell; online orders ship free across the United States.`,
    foundingDate: FOUNDING_YEAR,
    image: logoUrl,
    logo: logoUrl,
    url,
    telephone: schemaPhone(phone),
    email,
    priceRange: '$$',

    // ← CRITICAL for "near me" results
    address: {
      '@type': 'PostalAddress',
      streetAddress,
      addressLocality,
      addressRegion,
      postalCode,
      addressCountry: 'US',
    },

    // ← Geo coordinates for map pack ranking.
    // Only emitted when the configured address is still the Redmond office
    // these coordinates belong to. Previously they were unconditional, so when
    // the DB supplied a different address (production was serving a Kirkland
    // P.O. Box) the entity claimed one postal address and a set of coordinates
    // 6 miles away — contradictory NAP, which is corrosive for local ranking.
    ...(postalCode === '98052'
      ? { geo: { '@type': 'GeoCoordinates', latitude: 47.6868, longitude: -122.1162 } }
      : {}),

    // ← Links to Google Maps listing. Built from the SAME address fields as
    // `address` above rather than a hardcoded string, so the map link can never
    // again point somewhere the postal address doesn't.
    hasMap: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      `${name} ${streetAddress} ${addressLocality} ${addressRegion} ${postalCode}`
    )}`,

    // Hours come from the DB (the same values the Contact page shows). If the
    // text can't be parsed the property is omitted rather than guessed.
    ...(weekdayHours || saturdayHours
      ? {
          openingHoursSpecification: [
            ...(weekdayHours
              ? [{ '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], ...weekdayHours }]
              : []),
            ...(saturdayHours
              ? [{ '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], ...saturdayHours }]
              : []),
          ],
        }
      : {}),

    // National e-commerce + local Redmond service area
    areaServed: [
      { '@type': 'Country', name: 'United States' },
      ...EASTSIDE_CITIES.map((c) => ({
        '@type': 'City',
        name: c,
        containedInPlace: { '@type': 'State', name: 'Washington' },
      })),
    ],

    serviceType: [
      'Custom Window Blinds',
      'Motorized Blinds Installation',
      'Automated Security Blinds',
      'Smart Blind Installation Redmond',
      'Window Shades Installation',
      'Window Treatment Consultation',
      'AR Window Treatment Visualization',
      'Smart Home Blinds Integration',
    ],

    knowsAbout: [
      'Custom Window Treatments',
      'Motorized Blinds',
      'Smart Home Integration',
      'Window Covering Installation',
      'Patent-pending Smart Defense Security Blinds',
    ],

    currenciesAccepted: 'USD',
    paymentAccepted: 'Credit Card, Debit Card',
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// Product Schema
interface ProductJsonLdProps {
  name: string;
  description: string;
  image: string;
  sku?: string;
  price: number;
  priceCurrency?: string;
  availability?: 'InStock' | 'OutOfStock' | 'PreOrder';
  url: string;
  brand?: string;
  rating?: number;
  reviewCount?: number;
  category?: string;
}

export function ProductJsonLd({
  name,
  description,
  image,
  sku,
  price,
  priceCurrency = 'USD',
  availability = 'InStock',
  url,
  brand,
  rating,
  reviewCount,
  category,
}: ProductJsonLdProps) {
  const cfg = useSiteConfig();

  const { url: siteUrl, name: companyName } = seoIdentity(cfg);
  const brandName = brand || companyName;

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    description,
    image: image.startsWith('http') ? image : `${siteUrl}${image}`,
    sku: sku || name.toLowerCase().replace(/\s+/g, '-'),
    brand: {
      '@type': 'Brand',
      name: brandName,
    },
    category: category || 'Window Treatments',
    offers: {
      '@type': 'Offer',
      url,
      priceCurrency,
      price: price.toFixed(2),
      priceValidUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      availability: `https://schema.org/${availability}`,
      seller: {
        '@type': 'Organization',
        name: companyName,
      },
      shippingDetails: {
        '@type': 'OfferShippingDetails',
        shippingRate: {
          '@type': 'MonetaryAmount',
          value: '0',
          currency: 'USD',
        },
        shippingDestination: {
          '@type': 'DefinedRegion',
          addressCountry: 'US',
        },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          handlingTime: {
            '@type': 'QuantitativeValue',
            minValue: 1,
            maxValue: 3,
            unitCode: 'DAY',
          },
          transitTime: {
            '@type': 'QuantitativeValue',
            minValue: 5,
            maxValue: 10,
            unitCode: 'DAY',
          },
        },
      },
    },
  };

  // Add aggregate rating if available
  if (rating && reviewCount && reviewCount > 0) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: rating.toFixed(1),
      reviewCount,
      bestRating: '5',
      worstRating: '1',
    };
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// Breadcrumb Schema
interface BreadcrumbItem {
  name: string;
  url: string;
}

export function BreadcrumbJsonLd({ items }: { items: BreadcrumbItem[] }) {
  const cfg = useSiteConfig();
  const siteUrl = cfg.seoSiteUrl || cfg.siteUrl || seoFallback.url;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url.startsWith('http') ? item.url : `${siteUrl}${item.url}`,
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// FAQ Schema
interface FAQItem {
  question: string;
  answer: string;
}

export function FAQJsonLd({ items }: { items: FAQItem[] }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// How-To Schema (for guides)
interface HowToStep {
  name: string;
  text: string;
  image?: string;
}

export function HowToJsonLd({
  name,
  description,
  steps,
  totalTime,
  image,
}: {
  name: string;
  description: string;
  steps: HowToStep[];
  totalTime?: string; // ISO 8601 duration format, e.g., "PT30M"
  image?: string;
}) {
  const cfg = useSiteConfig();
  const siteUrl = cfg.seoSiteUrl || cfg.siteUrl || seoFallback.url;

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    description,
    step: steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
      ...(step.image && {
        image: {
          '@type': 'ImageObject',
          url: step.image.startsWith('http') ? step.image : `${siteUrl}${step.image}`,
        },
      }),
    })),
  };

  if (totalTime) {
    schema.totalTime = totalTime;
  }

  if (image) {
    schema.image = {
      '@type': 'ImageObject',
      url: image.startsWith('http') ? image : `${siteUrl}${image}`,
    };
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// Review Schema
export function ReviewJsonLd({
  itemReviewed,
  reviewRating,
  author,
  reviewBody,
  datePublished,
}: {
  itemReviewed: { name: string; type?: string };
  reviewRating: number;
  author: string;
  reviewBody: string;
  datePublished: string;
}) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Review',
    itemReviewed: {
      '@type': itemReviewed.type || 'Product',
      name: itemReviewed.name,
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: reviewRating,
      bestRating: '5',
      worstRating: '1',
    },
    author: {
      '@type': 'Person',
      name: author,
    },
    reviewBody,
    datePublished,
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// Service Schema (for measure & install)
export function ServiceJsonLd({
  name,
  description,
  provider,
  areaServed = 'United States',
  serviceType,
}: {
  name: string;
  description: string;
  provider?: string;
  areaServed?: string;
  serviceType?: string;
}) {
  const cfg = useSiteConfig();

  const companyName = cfg.companyName || seoFallback.name;
  const siteUrl = cfg.seoSiteUrl || cfg.siteUrl || seoFallback.url;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    provider: {
      '@type': 'Organization',
      name: provider || companyName,
      url: siteUrl,
    },
    areaServed: {
      '@type': 'Country',
      name: areaServed,
    },
    serviceType: serviceType || name,
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

// Collection/ItemList Schema (for product listings)
interface CollectionItem {
  name: string;
  url: string;
  image?: string;
  price?: number;
}

export function CollectionJsonLd({
  name,
  description,
  items,
}: {
  name: string;
  description?: string;
  items: CollectionItem[];
}) {
  const cfg = useSiteConfig();
  const siteUrl = cfg.seoSiteUrl || cfg.siteUrl || seoFallback.url;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name,
    description,
    numberOfItems: items.length,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Product',
        name: item.name,
        url: item.url.startsWith('http') ? item.url : `${siteUrl}${item.url}`,
        ...(item.image && {
          image: item.image.startsWith('http') ? item.image : `${siteUrl}${item.image}`,
        }),
        ...(item.price && {
          offers: {
            '@type': 'Offer',
            price: item.price.toFixed(2),
            priceCurrency: 'USD',
          },
        }),
      },
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}
